import { createTypedLogger } from '../../utils/typedLogger';

import axios from 'axios';
import moment from 'moment';
import { ApiConfig } from '../../types/apiConfig';
import axiosRetry from 'axios-retry';
import {
  CustomerItem,
  CustomerApiResponse,
  CustomerApiRequest,
  Status,
  VehicleItem,
  CaseStepsItem,
  CaseStepsApiRequest,
  CaseStepsApiResponse,
  VehicleApiResponse,
  VehicleApiRequest,
  LocationApiRequest,
  LocationApiResponse,
  CallDetailsItem,
  CallDetailsApiResponse,
  ServiceType,
  ClientName,
  LoginData,
  LoginApiRequest,
  LoginApiResponse,
  CaseDataResponse,
  CaseDataApiResponse,
  LocationItem,
  ProfileItem,
  ProfileApiResponse,
  ProfileApiRequest,
  DispatchCaseApiResponse,
  ClientPlatformApiResponse,
  ClientPlatformItem,
  CaseDataStepsApiResponse,
  CaseLogItem,
  VehicleMakeAPIResponse,
  ExtensionLogResponse,
  AgentExtensionLogsApiResponse,
  CaseCompletionResponse,
  GoogleMapCoordinatesData,
  E911ConnectAppRequest,
  E911ConnectAppResponse,
  AbstractConfigResponse,
} from './types';
import { ApiKeys } from '../../types/apiKeys';

export interface BackendApi {
  get_api_keys(): Promise<ApiKeys>;
  get_customerdata(query: string): Promise<CustomerItem>;
  post_customerdata(data: CustomerItem): Promise<CustomerItem>;
  put_customerdata(data: CustomerItem): Promise<CustomerItem | null>;
  get_vehicledata(query: string): Promise<VehicleItem>;
  post_vehicledata(data: VehicleItem): Promise<VehicleItem>;
  put_vehicledata(data: VehicleItem): Promise<VehicleItem>;
  get_locationdata(query: string): Promise<LocationItem | null>;
  post_locationdata(query: LocationItem): Promise<LocationApiResponse | null>;
  post_casestepsdata(data: CaseStepsItem): Promise<CaseStepsItem | null>;
  get_calldetailsdata(query: string): Promise<CallDetailsItem>;
  post_logindata(data: LoginData): Promise<LoginData>;
  get_logindata(query: string): Promise<LoginData>;
  get_casedata(
    case_id: string,
    phone_number: string,
    agent_extension: string,
  ): Promise<CaseDataResponse | null>;
  get_profilesdata(query: string): Promise<ProfileItem>;
  post_profilesdata(data: ProfileItem): Promise<ProfileItem | null>;
  put_profilesdata(data: ProfileItem): Promise<ProfileItem | null>;
  post_dispatchcase(
    case_id: string,
    agent_extension: string,
  ): Promise<DispatchCaseApiResponse | null>;
  get_vehicle_make(program: string | undefined): Promise<Array<string>>;
  makeGetCaseDataResponse(jsonData: any): CaseDataResponse;
  get_agent_extension_logs(
    email_address: string,
  ): Promise<ExtensionLogResponse>;
  post_complete_case(case_id: string): Promise<CaseCompletionResponse | null>;
  // e911 swoop APIs end
  get_google_map_coordinates(): Promise<GoogleMapCoordinatesData | null>;
  post_e911connectapp_createcase(
    data: E911ConnectAppRequest,
  ): Promise<E911ConnectAppResponse | null>;
  get_abstract_config_values(): Promise<AbstractConfigResponse | null>;
}

interface JSONApiResponse<T> {
  data: T;
}

export interface EventLogResponseItem {
  message: string;
  level: 'ERROR' | 'FATAL' | 'INFO' | 'WARN';
  date: string;
}

export interface EventLogResponse {
  events: EventLogResponseItem[];
}

export interface ApiKeysResponse {
  google_maps_key: string;
}

const customLogger = createTypedLogger('api');

export class API implements BackendApi {
  axios;
  logger;
  roadsideRequest: any;

  constructor(config: ApiConfig) {
    this.axios = axios.create(config);
    this.logger = customLogger;
    axiosRetry(this.axios, {
      retries: 2,
      retryDelay: axiosRetry.exponentialDelay,
      retryCondition: e => {
        return (
          e.request?.responseURL ===
            `${config.baseURL}${this.roadsideRequest}` &&
          (e.response?.status === 500 ||
            e.response?.status === 503 ||
            e.response?.status === 504)
        );
      },
    });
  }

  makeGetCaseDataResponse = (apiResponse: any) => {
    const response: CaseDataResponse = {
      callDetails: {},
      vehicle: {},
      customer: {},
      location: {},
      casesteps: [],
      status: 0,
      response_message: '',
      app_code: '',
    };
    let customerItem: CustomerItem = {};
    let vehicleItem: VehicleItem = {};
    let callDataItem: CallDetailsItem = {};
    let locationItem: LocationItem = {};
    let casestepsItem: CaseLogItem[] = [];
    const {
      status,
      response_message,
      location,
      vehicle,
      customer,
      casesteps,
      app_code,
    } = apiResponse;

    response.status = status;
    response.response_message = response_message;
    response.app_code = app_code;

    callDataItem = {
      service:
        ServiceType[apiResponse.case.service_type as keyof typeof ServiceType],
      program:
        ClientName[apiResponse.case.client_name as keyof typeof ClientName],
      caseId: apiResponse?.case?.case_id,
      phoneNumber: apiResponse?.case?.phone_number,
    };
    response.callDetails = callDataItem;

    if (customer) {
      customerItem = {
        address: customer?.address ? customer?.address : '',
        customerName: customer.customer_name ? customer?.customer_name : '',
        emailAddress: customer?.email_address ? customer?.email_address : '',
        phoneNumber: customer?.phone_number ? customer?.phone_number : '',
        preferredLanguage: customer?.preferred_language
          ? customer?.preferred_language
          : 'None',
        caseId: apiResponse?.case?.case_id,
      };
    }

    response.customer = customerItem;

    if (vehicle) {
      vehicleItem = {
        vehiclePhoneNumber: vehicle?.phone_number
          ? vehicle?.phone_number
          : 'None',
        vin: vehicle?.vin ? vehicle?.vin : 'None',
        model: vehicle?.model_name ? vehicle?.model_name : 'None',
        year: vehicle?.model_year ? vehicle?.model_year : 'None',
        color: vehicle?.color ? vehicle?.color : 'None',
        caseId: apiResponse?.case?.case_id,
      };
    }

    response.vehicle = vehicleItem;

    if (location) {
      locationItem = {
        caseId: apiResponse?.case?.case_id,
        latitude: location?.latitude,
        longitude: location?.longitude,
        timeStamp: location?.time_stamp,
        street: location?.location_address?.street,
        streetNumber: location?.location_address?.street_number,
        country: location?.location_address?.country,
        province: location?.location_address?.province,
        city: location?.location_address?.city,
        state: location?.location_address?.state,
        postalCode: location?.location_address?.postal_code,
        headingDirection: location?.location_address?.heading_direction,
      };
    }

    response.location = locationItem;

    if (casesteps?.length) {
      casestepsItem = casesteps.map((el: CaseDataStepsApiResponse) => {
        const timeStamp = moment
          .utc(el.time_stamp, 'MM/DD/YYYY HH:mm')
          .local()
          .format('MM/DD/YYYY HH:mm');

        const message = el.call_log.split(':');
        return {
          level: el.call_log_type,
          message: message[0],
          date: timeStamp,
          description: message.length === 2 ? message[1] : '',
        };
      });
    }
    response.casesteps = casestepsItem;

    return response;
  };

  async post_casestepsdata(data: CaseStepsItem): Promise<CaseStepsItem | null> {
    try {
      const body: CaseStepsApiRequest = {
        case_id: data.caseId,
        step_type: data.stepType,
        call_log: data.callLog,
        call_log_type: data.callLogType,
        time_stamp: moment().utc().format('MM/DD/YYYY HH:mm:ss.ssss'),
      };
      this.logger.debug('api request made', {
        action: 'postcasestepsdata',
        case_id: data.caseId,
      });

      const res = await this.axios.post<JSONApiResponse<CaseStepsApiResponse>>(
        `/cases/${data.caseId}/steps`,
        body,
      );

      this.logger.info('Response Received from API', {
        action: 'postcasestepsdata',
        res: res,
        caseId: data.caseId,
      });
      return make_post_casestep_response(res);
    } catch (err) {
      this.logger.error(`Failed to save case steps data: ${data.caseId}`, {
        error: err,
        action: 'postcasestepsdata',
        case_id: data.caseId,
      });
      return null;
    }
  }
  async get_vehicledata(case_id: string): Promise<VehicleItem> {
    let response: VehicleItem = {};
    try {
      this.logger.debug('get_vehicledata api request made', {
        action: 'getvehicledata',
        req: case_id,
      });
      const res = await this.axios.get<JSONApiResponse<VehicleApiResponse>>(
        `/cases/${case_id}/vehicle`,
      );

      if (res && res.status === Status.SUCCESS && res.data) {
        const { vin, phone_number, model_name, model_year, color } =
          res.data.data;

        response = {
          vin: vin,
          vehiclePhoneNumber: phone_number,
          model: model_name,
          year: model_year,
          color: color,
          caseId: case_id,
        };
        return response;
      }
      this.logger.info('Success: Get vehicle Data: ', {
        action: 'getvehicledata',
        case_id: case_id,
      });

      return response;
    } catch (err) {
      this.logger.error(`Failed to pull vehicle data: ${case_id}`, {
        action: 'getvehicledata',
        error: err,
      });
      return response;
    }
  }

  async post_vehicledata(data: VehicleItem): Promise<VehicleItem> {
    let response: VehicleItem = {};
    try {
      this.logger.debug('saveapi request made', {
        action: 'savevehicledata',
        case_id: data.caseId,
      });

      const body: VehicleApiRequest = {
        vin: data.vin,
        phone_number: data.vehiclePhoneNumber,
        model_name: data.model,
        model_year: data.year,
        color: data.color,
        brand: data.brand?.toLowerCase(),
      };

      const res = await this.axios.post<JSONApiResponse<VehicleApiResponse>>(
        `/cases/${data.caseId}/vehicle`,
        body,
      );
      if (res !== null && res.status === Status.CREATED) {
        const { vin, model_name, model_year, phone_number, color, case_id } =
          res.data.data;
        response = {
          vin: vin,
          vehiclePhoneNumber: phone_number,
          model: model_name,
          year: model_year,
          color: color,
          caseId: case_id,
        };
        this.logger.error(`Success: Save vehicle data: ${data.caseId}`, {
          action: 'savevehicledata',
          case_id: data.caseId,
        });

        return response;
      }

      this.logger.error(`Failed to save vehicle data: ${data.caseId}`, {
        response: res,
        action: 'savevehicledata',
        case_id: data.caseId,
      });

      return response;
    } catch (err) {
      this.logger.error(`Failed to save vehicle data: ${data.caseId}`, {
        error: err,
        action: 'savevehicledata',
        case_id: data.caseId,
      });
      return response;
    }
  }

  async put_vehicledata(data: VehicleItem): Promise<VehicleItem> {
    let response: VehicleItem = {};
    try {
      this.logger.info('updateapi request made', {
        action: 'updatevehicledata',
        case_id: data.caseId,
      });

      if (data.year === 'None') {
        data.year = 0;
      }
      const body: VehicleApiRequest = {
        vin: data.vin,
        phone_number: data.vehiclePhoneNumber,
        model_name: data.model,
        model_year: data.year,
        color: data.color,
        brand: data.brand?.toLowerCase(),
      };

      const res = await this.axios.put<JSONApiResponse<VehicleApiResponse>>(
        `/cases/${data.caseId}/vehicle`,
        body,
      );
      if (res !== null && res.status === Status.SUCCESS) {
        const { vin, model_name, model_year, phone_number, color, case_id } =
          res.data.data;
        response = {
          vin: vin,
          vehiclePhoneNumber: phone_number,
          model: model_name,
          year: model_year,
          color: color,
          caseId: case_id,
        };
        this.logger.info(`Success: Update vehicle data: ${data.caseId}`, {
          action: 'updatevehicledata',
          case_id: data.caseId,
        });

        return response;
      }

      return response;
    } catch (err) {
      this.logger.error(`Failed to update vehicle data: ${data.caseId}`, {
        error: err,
        action: 'updatevehicledata',
        case_id: data.caseId,
      });
      return response;
    }
  }

  async get_customerdata(case_id: string): Promise<CustomerItem> {
    let response: CustomerItem = {};

    try {
      this.logger.debug('api request made', {
        action: 'getcustomerdata',
        case_id: case_id,
      });

      const res = await this.axios.get<JSONApiResponse<CustomerApiResponse>>(
        `/cases/${case_id}/customer`,
      );

      if (res && res.status === Status.SUCCESS && res.data) {
        const {
          address,
          customer_name,
          email_address,
          phone_number,
          preferred_language,
        } = res.data.data;

        response = {
          address: address,
          customerName: customer_name,
          emailAddress: email_address,
          phoneNumber: phone_number,
          preferredLanguage: preferred_language,
          caseId: case_id,
        };
      }
      this.logger.info('Success: Get customer Data: ', {
        action: 'getcustomerdata',
        case_id: case_id,
      });

      return response;
    } catch (err) {
      this.logger.error(`Failed to pull customer data: ${case_id}`, {
        error: err,
        action: 'getcustomerdata',
        case_id: case_id,
      });
      return response;
    }
  }

  async post_customerdata(data: CustomerItem): Promise<CustomerItem> {
    let response: CustomerItem = {};

    try {
      this.logger.debug('post_customerdata request made', {
        action: 'savecustomerdata',
        case_id: data.caseId,
      });
      const body: CustomerApiRequest = {
        address: data.address,
        case_id: data.caseId,
        customer_name: data.customerName,
        email_address: data.emailAddress,
        preferred_language: data.preferredLanguage,
        phone_number: data.phoneNumber,
        time_stamp: moment().utc().format('MM/DD/YYYY HH:mm:ss.ssss'),
      };
      const res = await this.axios.post<JSONApiResponse<CustomerApiResponse>>(
        `/cases/${data.caseId}/customer`,
        body,
      );
      if (res !== null && res.status === Status.CREATED) {
        const {
          address,
          customer_name,
          email_address,
          phone_number,
          preferred_language,
          case_id,
        } = res.data.data;

        response = {
          address: address,
          customerName: customer_name,
          emailAddress: email_address,
          phoneNumber: phone_number,
          preferredLanguage: preferred_language,
          caseId: case_id,
        };
        this.logger.info('Success: Save Customer Data: ', {
          response: res,
          action: 'savecustomerdata',
          case_id: data.caseId,
        });
        return response;
      }
      return response;
    } catch (err) {
      this.logger.error(`Failed: to save customer data: ${data.caseId}`, {
        error: err,
        action: 'savecustomerdata',
        case_id: data.caseId,
      });
      return response;
    }
  }

  async put_customerdata(data: CustomerItem): Promise<CustomerItem | null> {
    try {
      this.logger.debug('put_customerdata request made', {
        action: 'updatecustomerdata',
        case_id: data.caseId,
      });
      const body: CustomerApiRequest = {
        address: data.address,
        case_id: data.caseId,
        customer_name: data.customerName,
        email_address: data.emailAddress,
        preferred_language: data.preferredLanguage,
        phone_number: data.phoneNumber,
        time_stamp: moment().utc().format('MM/DD/YYYY HH:mm:ss.ssss'),
      };
      const res = await this.axios.put<JSONApiResponse<CustomerApiResponse>>(
        `/cases/${data.caseId}/customer`,
        body,
      );
      if (res !== null && res.status === Status.SUCCESS) {
        const {
          address,
          customer_name,
          email_address,
          phone_number,
          preferred_language,
          case_id,
        } = res.data.data;
        let response: CustomerItem = {};
        response = {
          address: address,
          customerName: customer_name,
          emailAddress: email_address,
          phoneNumber: phone_number,
          preferredLanguage: preferred_language,
          caseId: case_id,
        };
        this.logger.info('Success: Updated Customer Data: ', {
          response: res,
          action: 'updatecustomerdata',
          case_id: data.caseId,
        });
        return response;
      }
      return null;
    } catch (err) {
      this.logger.error(`Failed to update customer data: ${data.caseId}`, {
        error: err,
        action: 'updatecustomerdata',
        case_id: data.caseId,
      });
      return null;
    }
  }

  async get_api_keys(): Promise<ApiKeys> {
    try {
      const data = (
        await this.axios.get<JSONApiResponse<ApiKeysResponse>>('/api_keys')
      ).data.data;
      return {
        googleMapsKey: data.google_maps_key,
      } as ApiKeys;
    } catch (err) {
      this.logger.error(`Failed to get google keys`, {
        error: err,
      });
      return { googleMapsKey: '' };
    }
  }

  async get_locationdata(caseid: string): Promise<LocationItem | null> {
    try {
      this.logger.debug('api request made', {
        action: 'getlocationdata',
        case_id: caseid,
      });

      const res = await this.axios.get<JSONApiResponse<LocationApiResponse>>(
        `/cases/${caseid}/location`,
      );

      if (res && res.status === Status.SUCCESS && res.data) {
        this.logger.info('Success: Get Location Data: ', {
          action: 'getlocationdata',
          case_id: caseid,
        });
        const { case_id, latitude, longitude, location_address } =
          res.data.data;

        const response: LocationItem = {
          caseId: case_id,
          latitude: latitude,
          longitude: longitude,
          street: location_address?.street,
          streetNumber: location_address?.street_number,
          country: location_address?.country,
          province: location_address?.province,
          city: location_address?.city,
          state: location_address?.state,
          postalCode: location_address?.postal_code,
          headingDirection: location_address?.heading_direction,
        };
        return response;
      }
      return null;
    } catch (err) {
      this.logger.error(`Failed to pull location data: ${caseid}`, {
        error: err,
        action: 'getlocationdata',
        case_id: caseid,
      });
      return null;
    }
  }

  async post_locationdata(
    data: LocationItem,
  ): Promise<LocationApiResponse | null> {
    try {
      this.logger.debug('save api request made', {
        action: 'savelocationdata',
        case_id: data.caseId,
      });
      const body = locationApiRequest(data);
      const res = await this.axios.post<JSONApiResponse<LocationApiResponse>>(
        `/cases/${data.caseId}/location`,
        body,
      );
      this.logger.info('Response Received from API', {
        action: 'postlocationdata',
        res: res,
        agent_extension: data.agentextension,
        caseId: data.caseId,
      });
      return make_post_location_response(res, data);
    } catch (err) {
      this.logger.error(`Failed to save location data: ${data.caseId}`, {
        error: err,
        action: 'postlocationdata',
        case_id: data.caseId,
      });
      return null;
    }
  }

  async get_calldetailsdata(case_id: string): Promise<CallDetailsItem> {
    let response: CallDetailsItem = {};
    try {
      this.logger.debug('get_calldetailsdata api request made', {
        action: 'getcalldetailsdata',
        req: case_id,
      });
      const res = await this.axios.get<JSONApiResponse<CallDetailsApiResponse>>(
        `/cases/${case_id}`,
      );

      if (res && res.status === Status.SUCCESS && res.data) {
        const { service_type, client_name } = res.data.data;
        response = {
          program: ClientName[client_name as keyof typeof ClientName],
          service: ServiceType[service_type as keyof typeof ServiceType],
          caseId: case_id,
        };
        return response;
      }
      this.logger.info('Success: Get Call details Data: ', {
        action: 'getcalldetailsdata',
        case_id: case_id,
      });
      return response;
    } catch (err) {
      this.logger.error(`Failed to pull Get Call data: ${case_id}`, {
        action: 'getcalldetailsdata',
        error: err,
      });
      return response;
    }
  }

  async get_casedata(
    case_id: string,
    phone_number: string,
    agent_extension: any,
  ): Promise<CaseDataResponse | null> {
    let response: CaseDataResponse = {
      callDetails: {},
      vehicle: {},
      customer: {},
      location: {},
      casesteps: [],
      status: 0,
      response_message: 'Init',
      app_code: '',
    };
    try {
      this.logger.debug('get_casedata api request made', {
        action: 'getcasedata',
        req: case_id,
        agent_extension,
        phone_number,
      });
      const caseId = case_id ? case_id : null;
      const res = await this.axios.get<JSONApiResponse<CaseDataApiResponse>>(
        `/cases/${caseId}/agent_extension/${agent_extension}/detail?phone_number=${phone_number}`,
      );
      if (res && res.status === Status.SUCCESS && res.data) {
        response = this.makeGetCaseDataResponse(res.data.data);
      }
      this.logger.info('Success: Get Complete Case Data: ', {
        action: 'getcasedata',
        case_id: case_id,
        agent_extension: agent_extension,
        phone_number: phone_number,
      });
      return response;
    } catch (err) {
      this.logger.error(
        `Failed to pull completeCase data: ${case_id} and ${agent_extension} and ${phone_number}`,
        {
          action: 'getcasedata',
          error: err,
        },
      );
      return response;
    }
  }

  async post_logindata(data: LoginData): Promise<LoginData> {
    let response: LoginData = {};
    try {
      this.logger.debug('save api request made', {
        action: 'postlogindata',
      });
      const body: LoginApiRequest = {
        email_address: data.email_address,
        agent_extension: data.agent_extension,
        login_time_stamp: moment().utc().format('MM/DD/YYYY HH:mm:ss.ssss'),
        extension_logs: data.extension_logs ? data.extension_logs : null,
        reason: data.reason ? data.reason : 'NA',
      };
      const res = await this.axios.post<JSONApiResponse<LoginApiResponse>>(
        `/login`,
        body,
      );
      if (res !== null && res.status === Status.CREATED && res.data) {
        this.logger.info('Success: Save Login Data ', {
          action: 'postlogindata',
        });
        const { agent_email, agent_extension } = res.data.data;
        response = {
          email_address: agent_email,
          agent_extension: agent_extension,
        };
      }
      return response;
    } catch (err) {
      this.logger.error(`Failed to save Login data for ${data.email_address}`, {
        error: err,
        action: 'postlogindata',
        agent_email: data.email_address,
      });
      return response;
    }
  }
  async get_logindata(user_email: string): Promise<LoginData> {
    let response: LoginData = {};
    try {
      this.logger.debug('api request made', {
        action: 'getlogindata',
        user_email: user_email,
      });

      const res = await this.axios.get<JSONApiResponse<LoginApiResponse>>(
        `/login/${user_email}`,
      );

      if (res && res.status === Status.SUCCESS && res.data) {
        this.logger.info('Success: Get login Data: ', {
          action: 'getlogindata',
          user_email: user_email,
        });
        const { agent_email, agent_extension } = res.data.data;
        response = {
          email_address: agent_email,
          agent_extension: agent_extension,
        };
      }
      return response;
    } catch (err) {
      this.logger.error(`Failed to pull login data: ${user_email}`, {
        error: err,
        action: 'getlogindata',
        user_email: user_email,
      });
      return response;
    }
  }

  async get_profilesdata(emailAddress: string): Promise<ProfileItem> {
    // #TODO: this is the static value deafult for user profile. please update if any new client onboarded
    const defaultClientPlatformItemToyota: ClientPlatformItem = {
      clientName: 'toyota',
      clientPlatformType: 'wirelesscar',
      userName: '',
      notApplicable: true,
      clientLabel: 'Toyota User Name',
    };

    const defaultClientPlatformItemSubaru: ClientPlatformItem = {
      clientName: 'subaru',
      clientPlatformType: 'wirelesscar',
      userName: '',
      notApplicable: true,
      clientLabel: 'Subaru User Name',
    };

    const response: ProfileItem = {
      emailAddress: emailAddress,
      clientPlatform: [
        defaultClientPlatformItemSubaru,
        defaultClientPlatformItemToyota,
      ],
      isComplete: false,
    };

    try {
      this.logger.debug('get_profilesdata api request made', {
        action: 'getprofilesdata',
        req: emailAddress,
      });
      const res = await this.axios.get<JSONApiResponse<ProfileApiResponse>>(
        `/profiles/${emailAddress}`,
      );

      if (res && res.status === Status.SUCCESS && res.data) {
        this.logger.info('Success: Get Profile details Data: ', {
          action: 'getprofilesdata',
          email_address: emailAddress,
        });
      }
      return makeProfileDataResponse(res);
    } catch (err) {
      this.logger.error(`Failed to pull Get Profile data: ${emailAddress}`, {
        action: 'getprofilesdata',
        error: err,
      });
      return response;
    }
  }

  async post_profilesdata(data: ProfileItem): Promise<ProfileItem | null> {
    try {
      this.logger.debug('post_profilesdata api request made', {
        action: 'postprofilesdata',
        req: data.emailAddress,
      });
      const body: ProfileApiRequest = {
        email_address: data.emailAddress,
        client_platforms: makeFormattedClientPlatform(data.clientPlatform),
      };
      const res = await this.axios.post<JSONApiResponse<ProfileApiResponse>>(
        `/profiles`,
        body,
      );
      if (res !== null && res.status === Status.CREATED && res.data) {
        this.logger.info('Success: Saved Profile details Data: ', {
          action: 'postprofilesdata',
          email_address: data.emailAddress,
        });
        return makeProfileDataResponse(res);
      }
      return null;
    } catch (err) {
      this.logger.error(`Failed to save Profile data: ${data.emailAddress}`, {
        action: 'postprofilesdata',
        error: err,
      });
      return null;
    }
  }

  async put_profilesdata(data: ProfileItem): Promise<ProfileItem | null> {
    try {
      this.logger.debug('put_profilesdata api request made', {
        action: 'putprofilesdata',
        req: data.emailAddress,
      });
      const body: ProfileApiRequest = {
        email_address: data.emailAddress,
        client_platforms: makeFormattedClientPlatform(data.clientPlatform),
      };
      const res = await this.axios.put<JSONApiResponse<ProfileApiResponse>>(
        `/profiles`,
        body,
      );
      if (res !== null && res.status === Status.SUCCESS && res.data) {
        this.logger.info('Success: Updated Profile details Data: ', {
          action: 'putprofilesdata',
          email_address: data.emailAddress,
        });
        return makeProfileDataResponse(res);
      }
      return null;
    } catch (err) {
      this.logger.error(`Failed to Update Profile data: ${data.emailAddress}`, {
        action: 'putprofilesdata',
        error: err,
      });
      return null;
    }
  }

  async post_dispatchcase(
    case_id: string,
    agent_extension: string,
  ): Promise<DispatchCaseApiResponse | null> {
    try {
      this.logger.debug('api request made', {
        action: 'postdispatchcase',
        case_id: case_id,
        agent_extension: agent_extension,
      });

      this.roadsideRequest = `/cases/${case_id}/roadside_request/${agent_extension}`;

      const res = await this.axios.post<
        JSONApiResponse<DispatchCaseApiResponse>
      >(`/cases/${case_id}/roadside_request/${agent_extension}`);

      if (res !== null && res.status === Status.CREATED && res.data) {
        this.logger.info('Success: Post Dispatch Case: ', {
          action: 'postdispatchcase',
          case_id: case_id,
          agent_extension: agent_extension,
          res_data: res.data,
          res_status: res.status,
        });
        return res.data.data;
      }
      return null;
    } catch (err) {
      this.logger.error(`Failed to save dispatch case: ${case_id}`, {
        error: err,
        action: 'postdispatchcase',
        case_id: case_id,
        agent_extension: agent_extension,
      });

      return null;
    }
  }

  async get_vehicle_make(program: string | undefined): Promise<Array<string>> {
    try {
      this.logger.debug('get vehicle make request made', {
        action: 'get_vehicle_makes',
      });
      if (program === '' || program === undefined) {
        return [];
      }
      const res = await this.axios.get<VehicleMakeAPIResponse>(
        `vehicle_makes/${program}`,
      );
      return res.data.data;
    } catch (err) {
      this.logger.error(`Failed to pull data`, {
        action: 'get_vehicle_makes',
        error: err,
      });
      return [];
    }
  }

  async get_agent_extension_logs(
    email_address: string,
  ): Promise<ExtensionLogResponse> {
    let response: ExtensionLogResponse = {};

    try {
      this.logger.debug('api request made', {
        action: 'get_agent_extension_logs',
        email_address: email_address,
      });

      const res = await this.axios.get<
        JSONApiResponse<AgentExtensionLogsApiResponse>
      >(`/agent-extension/${email_address}`);

      if (res && res.status === Status.SUCCESS && res.data) {
        const { extension_log } = res.data.data;
        this.logger.info('Success: Get agent_extension_logs ', {
          action: 'get_agent_extension_logs',
          email_address: email_address,
          data: extension_log,
        });

        response = {
          extension: extension_log?.extension
            ? extension_log?.extension.toString()
            : '',
          email: extension_log?.email ? extension_log?.email : '',
          check_in_time: extension_log?.check_in_time
            ? extension_log?.check_in_time
            : '',
          duration: extension_log?.duration ? extension_log?.duration : 0,
          check_out_time: extension_log?.check_out_time
            ? extension_log?.check_out_time
            : '',
          release_time: extension_log?.release_time
            ? extension_log?.release_time
            : '',
          modified_by: extension_log?.modified_by
            ? extension_log?.modified_by
            : '',
          modified_date: extension_log?.modified_date
            ? extension_log?.modified_date
            : '',
          can_release: extension_log?.can_release
            ? extension_log?.can_release
            : false,
          can_modify: extension_log?.can_modify
            ? extension_log?.can_modify
            : false,
          first_name: extension_log?.first_name
            ? extension_log?.first_name
            : '',
          last_name: extension_log?.last_name ? extension_log?.last_name : '',
        };
      }
      return response;
    } catch (err) {
      this.logger.error(
        `Failed to pull agent_extension_logs: ${email_address}`,
        {
          error: err,
          action: 'get_agent_extension_logs',
          email_address: email_address,
        },
      );
      return response;
    }
  }

  async post_complete_case(
    case_id: string,
  ): Promise<CaseCompletionResponse | null> {
    try {
      this.logger.debug('api request made', {
        action: 'post_complete_case',
        case_id: case_id,
      });

      const res = await this.axios.post<
        JSONApiResponse<DispatchCaseApiResponse>
      >(`/cases/${case_id}/completion`);

      if (res && res.status === Status.SUCCESS && res.data) {
        this.logger.info('Success: Post Case Completed: ', {
          action: 'post_complete_case',
        });
        return res.data.data;
      }
      return null;
    } catch (err) {
      this.logger.error(`Failed to save Case Completed case: ${case_id}`, {
        error: err,
        action: 'post_complete_case',
      });

      return null;
    }
  }

  async get_google_map_coordinates(): Promise<GoogleMapCoordinatesData | null> {
    let response: GoogleMapCoordinatesData = {
      is_google_map_coordinates: false,
      google_map_coordinates: [],
    };

    try {
      this.logger.debug('get_google_map_coordinates request made', {
        action: 'get_google_map_coordinates',
      });

      const res = await this.axios.get<
        JSONApiResponse<GoogleMapCoordinatesData>
      >('/abstract/get/google-map-coordinates');

      if (res !== null && res.status === Status.SUCCESS) {
        const { google_map_coordinates, is_google_map_coordinates } =
          res.data.data;
        response = {
          google_map_coordinates: google_map_coordinates,
          is_google_map_coordinates: is_google_map_coordinates,
        };
        this.logger.info('Success: get_google_map_coordinates: ', {
          response: res,
          action: 'get_google_map_coordinates',
        });
        return response;
      }
      return response;
    } catch (err) {
      this.logger.error(`Failed: to get get_google_map_coordinates`, {
        error: err,
        action: 'get_google_map_coordinates',
      });
      return response;
    }
  }

  async post_e911connectapp_createcase(
    data: E911ConnectAppRequest,
  ): Promise<E911ConnectAppResponse | null> {
    try {
      this.logger.debug('post_e911connectapp_createcase api request made', {
        action: 'post_e911connectapp_createcase',
        case_id: data.incidentId,
        agent_extension: data.agentExtension,
      });
      const res = await this.axios.post<
        JSONApiResponse<E911ConnectAppResponse>
      >(`/e911-connect-app/create-case/${data.incidentId}`, data);
      this.logger.info('Response Received from API', {
        action: 'post_e911connectapp_createcase',
        res: res,
        agent_extension: data.agentExtension,
        case_id: data.incidentId,
      });
      if (res !== null && res.status && res.data) {
        return res.data.data;
      }
      return null;
    } catch (err) {
      this.logger.error(
        `Failed to make post_e911connectapp_createcase with case_id ${data.incidentId}`,
        {
          error: err,
          action: 'post_e911connectapp_createcase',
          case_id: data.incidentId,
          agent_extension: data.agentExtension,
        },
      );
      return null;
    }
  }

  async get_abstract_config_values(): Promise<AbstractConfigResponse | null> {
    try {
      this.logger.debug('get_abstract_config_values request made', {
        action: 'get_abstract_config_values',
      });

      const res = await this.axios.get<JSONApiResponse<AbstractConfigResponse>>(
        '/abstract/get/abstract-config-values',
      );

      if (res !== null && res.status === Status.SUCCESS) {
        this.logger.info('Success: get_abstract_config_values: ', {
          response: res,
          action: 'get_abstract_config_values',
        });
        return res.data.data;
      }
      return null;
    } catch (err) {
      this.logger.error(`Failed: to get get_abstract_config_values`, {
        error: err,
        action: 'get_abstract_config_values',
      });
      return null;
    }
  }
}

function makeProfileDataResponse(apiResponse: any) {
  // this function is used for making profile data reponse from profile api's
  const { email_address, time_stamp, is_complete, client_platforms } =
    apiResponse.data.data;

  return {
    emailAddress: email_address,
    eventTime: time_stamp,
    isComplete: is_complete,
    clientPlatform: client_platforms.map(
      (client: ClientPlatformApiResponse) => {
        const label = client.client_name;
        return {
          userName: client.user_name,
          notApplicable: !client.is_applicable,
          clientName: client.client_name,
          clientPlatformType: client.client_platform_type,
          clientLabel: `${
            label.charAt(0).toUpperCase() + label.slice(1)
          } User Name`,
        };
      },
    ),
  };
}

function makeFormattedClientPlatform(data: ClientPlatformItem[]) {
  return data.map((client: ClientPlatformItem) => {
    return {
      user_name: client.userName,
      is_applicable: !client.notApplicable,
      client_name: client.clientName,
      client_platform_type: client.clientPlatformType,
    };
  });
}

function make_post_casestep_response(res: any) {
  if (res && res.status === Status.CREATED && res.data) {
    const { case_id, step_type, call_log, call_log_type, time_stamp } =
      res.data.data;
    const response: CaseStepsItem = {
      caseId: case_id,
      stepType: step_type,
      callLog: call_log,
      callLogType: call_log_type,
      timeStamp: time_stamp,
    };
    return response;
  }
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function make_post_location_response(res: any, data: LocationItem) {
  if (res !== null && res.status === Status.CREATED && res.data) {
    return res.data.data;
  }
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars

function locationApiRequest(data: any) {
  const body: LocationApiRequest = {
    latitude: data.latitude,
    longitude: data.longitude,
    location_address: {
      heading_direction: data.headingDirection,
      street: data.street,
      street_number: data.streetNumber,
      postal_code: data.postalCode,
      city: data.city,
      province: data.province,
      state: data.state,
      country: data.country,
    },
    time_stamp: moment().utc().format('MM/DD/YYYY HH:mm:ss.ssss'),
  };
  return body;
}
